// ── Act-As Session (P12.2b) ──
// SuperAdmin-only escalation from read-only Observer Mode to write-enabled
// "Act As" mode. Pre-beta QA only.
//
// Hard rules:
//   • sessionStorage ONLY — auto-clears on tab close.
//   • 10-minute window, then auto-expires.
//   • Requires typed CONFIRM + reason (≥10 chars). Enforced by UI.
//   • Writes go through canonical writers untouched (no invariant bypass).
//   • Telemetry stays tagged `observer: true` so real metrics exclude
//     SuperAdmin acts. The `reason` and `actor` are captured here for
//     audit only and never leaked into pilot KPIs.
//   • Role-agnostic plumbing — caller (`useObserverMode`) gates by role.

import type { ObserverKind } from "@/data/observerMode/observerSession";

export const ACT_AS_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
export const ACT_AS_REASON_MIN = 10;
export const ACT_AS_CONFIRM_PHRASE = "CONFIRM";

const KEYS: Record<ObserverKind, string> = {
  contractor: "koredor.observer.actAs.contractor",
  installer: "koredor.observer.actAs.installer",
};

export interface ActAsSession {
  id: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
}

function safeSession(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function read(kind: ObserverKind): ActAsSession | null {
  const s = safeSession();
  if (!s) return null;
  try {
    const raw = s.getItem(KEYS[kind]);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActAsSession;
    if (!parsed?.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getActAsSession(kind: ObserverKind): ActAsSession | null {
  const sess = read(kind);
  if (!sess) return null;
  if (Date.parse(sess.expiresAt) <= Date.now()) {
    clearActAsSession(kind);
    return null;
  }
  return sess;
}

export function isActAsActive(kind: ObserverKind, observedId: string | null): boolean {
  if (!observedId) return false;
  const sess = getActAsSession(kind);
  return !!sess && sess.id === observedId;
}

export function startActAsSession(
  kind: ObserverKind,
  id: string,
  reason: string,
): ActAsSession | null {
  const s = safeSession();
  if (!s || !id) return null;
  const trimmed = reason.trim();
  if (trimmed.length < ACT_AS_REASON_MIN) return null;
  const now = Date.now();
  const session: ActAsSession = {
    id,
    reason: trimmed,
    startedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + ACT_AS_WINDOW_MS).toISOString(),
  };
  try {
    s.setItem(KEYS[kind], JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

export function clearActAsSession(kind: ObserverKind): void {
  const s = safeSession();
  if (!s) return;
  try {
    s.removeItem(KEYS[kind]);
  } catch {
    /* noop */
  }
}

export function clearAllActAsSessions(): void {
  clearActAsSession("contractor");
  clearActAsSession("installer");
}

/** Milliseconds remaining in the active window, or 0 if none. */
export function getActAsRemainingMs(kind: ObserverKind): number {
  const sess = getActAsSession(kind);
  if (!sess) return 0;
  return Math.max(0, Date.parse(sess.expiresAt) - Date.now());
}
